// fetchFocusQueue prepares the ordered list of focus-mode tasks.
// It gathers raw tasks, applies the agreed sorting rules, and attaches time estimates.

import { db, TaskWithContext } from '../../lib/db';
import {
  TaskQueueCategory,
  normalizeDate,
  getRegularDueDate,
  getDeliverableDeadline,
  buildEstimateKey,
  priorityRank
} from './focusUtils';

export async function fetchFocusQueue(
  dbUserId: string,
  completedTaskIds: ReadonlySet<string>
): Promise<TaskWithContext[]> {
  const [tasksWithContext, todaysRegularTasksWithContext, overdueRegularTasksWithContext] = await Promise.all([
    db.tasks.findTasksWithContext(dbUserId),
    db.tasks.findTodaysRegularTasksWithContext(dbUserId),
    db.tasks.findOverdueRegularTasksWithContext(dbUserId)
  ]);

  const seenTaskIds = new Set<string>();
  const midnightToday = new Date();
  midnightToday.setHours(0, 0, 0, 0);

  const shouldSkipTask = (task: TaskWithContext) => {
    if (seenTaskIds.has(task.id)) return true;
    if (completedTaskIds.has(task.id)) return true;
    if (task.status === 'Complete' || task.status === 'Superseded') return true;
    if (task.notes && task.notes.includes('[Deferred to Tomorrow]')) return true;
    if (!task.is_regular && !task.is_live) return true;
    if (task.is_regular) {
      const createdAt = normalizeDate(task.created_at);
      if (createdAt) {
        const fiveSecondsAgo = Date.now() - 5000;
        if (createdAt.getTime() > fiveSecondsAgo) {
          return true;
        }
      }
    }
    return false;
  };

  const prepareTask = (task: TaskWithContext, category: TaskQueueCategory): TaskWithContext => {
    const dueDate = category === 'deliverable' ? getDeliverableDeadline(task) : getRegularDueDate(task);
    const isOverdue =
      category === 'overdue' ||
      (category === 'regular' && !!dueDate && dueDate.getTime() < midnightToday.getTime());
    return {
      ...task,
      queueCategory: category,
      queueDueDate: dueDate,
      isOverdue
    };
  };

  const collectTasks = (source: TaskWithContext[], category: TaskQueueCategory) => {
    const bucket: TaskWithContext[] = [];
    for (const task of source) {
      if (shouldSkipTask(task)) {
        continue;
      }
      seenTaskIds.add(task.id);
      bucket.push(prepareTask(task, category));
    }
    return bucket;
  };

  const overdueRegular = collectTasks(overdueRegularTasksWithContext, 'overdue').sort((a, b) => {
    const aDue = a.queueDueDate ? a.queueDueDate.getTime() : Number.MIN_SAFE_INTEGER;
    const bDue = b.queueDueDate ? b.queueDueDate.getTime() : Number.MIN_SAFE_INTEGER;
    if (aDue !== bDue) return aDue - bDue;
    const priorityDiff = priorityRank(a.priority) - priorityRank(b.priority);
    if (priorityDiff !== 0) return priorityDiff;
    return a.title.localeCompare(b.title);
  });

  const regularTasks = collectTasks(todaysRegularTasksWithContext, 'regular').sort((a, b) => {
    const priorityDiff = priorityRank(a.priority) - priorityRank(b.priority);
    if (priorityDiff !== 0) return priorityDiff;
    const aDue = a.queueDueDate ? a.queueDueDate.getTime() : Number.MAX_SAFE_INTEGER;
    const bDue = b.queueDueDate ? b.queueDueDate.getTime() : Number.MAX_SAFE_INTEGER;
    if (aDue !== bDue) return aDue - bDue;
    return a.title.localeCompare(b.title);
  });

  const deliverableCandidates = tasksWithContext.filter(task => !task.is_regular);
  const deliverableTasks = collectTasks(deliverableCandidates, 'deliverable').sort((a, b) => {
    const aDue = a.queueDueDate ? a.queueDueDate.getTime() : Number.MAX_SAFE_INTEGER;
    const bDue = b.queueDueDate ? b.queueDueDate.getTime() : Number.MAX_SAFE_INTEGER;
    if (aDue !== bDue) return aDue - bDue;
    const priorityDiff = priorityRank(a.priority) - priorityRank(b.priority);
    if (priorityDiff !== 0) return priorityDiff;
    return a.title.localeCompare(b.title);
  });

  const orderedQueue = [...overdueRegular, ...regularTasks, ...deliverableTasks];

  const estimateIdentifiers = orderedQueue.map(task => ({
    title: task.title,
    description: task.description || ''
  }));

  const estimates = orderedQueue.length
    ? await db.taskCompletionStatistics.getBatchEstimates(estimateIdentifiers, dbUserId)
    : new Map<string, number>();

  return orderedQueue.map(task => {
    const estimate = estimates.get(buildEstimateKey(task));
    return {
      ...task,
      time_estimate: estimate ?? undefined,
      estimatedMinutes: estimate ?? undefined
    };
  });
}
